import {
  Button, Space, Tag, Tooltip, message, Breadcrumb,
  Input, Select,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  HomeOutlined, SearchOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { clinicApi } from '../../../api/clinic.api';
import DataTable from '../../../components/common/DataTable';
import ClinicForm from '../components/ClinicForm';
import { showDeleteConfirm } from '../../../components/common/ConfirmModal';
import useDebounce from '../../../hooks/useDebounce';

// ─── Main Component ────────────────────────────────────────────────
const ClinicList = () => {
  const [formOpen,     setFormOpen]     = useState(false);
  const [editRecord,   setEditRecord]   = useState(null);
  const [searchInput,  setSearchInput]  = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination,   setPagination]   = useState({ page: 1, limit: 10 });
  const [sort,         setSort]         = useState({ sortBy: 'createdAt', sortOrder: 'desc' });

  const queryClient = useQueryClient();
  const navigate    = useNavigate();

  const debouncedSearch = useDebounce(searchInput, 300);

  // ── Data fetch ─────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['clinics', debouncedSearch, statusFilter, sort, pagination],
    queryFn:  () => clinicApi.getAll({
      search: debouncedSearch,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...sort,
      ...pagination,
    }),
  });

  // ── Mutations ──────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: clinicApi.create,
    onSuccess: () => { queryClient.invalidateQueries(['clinics']); message.success('Clinic created'); setFormOpen(false); },
    onError: (err) => message.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => clinicApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['clinics']);
      message.success('Clinic updated');
      setFormOpen(false);
      setEditRecord(null);
    },
    onError: (err) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: clinicApi.delete,
    onSuccess: () => { queryClient.invalidateQueries(['clinics']); message.success('Clinic deleted'); },
    onError: (err) => message.error(err.message),
  });

  const handleFormSubmit = async (values) => {
    try {
      if (editRecord) { await updateMutation.mutateAsync({ id: editRecord.id, data: values }); }
      else            { await createMutation.mutateAsync(values); }
    } catch { /* error shown by onError */ }
  };

  // ── Search ────────────────────────────────────────────────────
  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handleTableChange = useCallback((_pagination, _filters, sorter, extra) => {
    if (extra?.action !== 'sort') { return; }

    const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    setSort(nextSorter?.order
      ? {
          sortBy: nextSorter.field || nextSorter.columnKey,
          sortOrder: nextSorter.order === 'ascend' ? 'asc' : 'desc',
        }
      : { sortBy: 'createdAt', sortOrder: 'desc' });
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  // ── Derived data ───────────────────────────────────────────────
  const allClinics = data?.data || [];
  const meta       = data?.meta || {};

  const getSortOrder = (field) => (
    sort.sortBy === field ? (sort.sortOrder === 'asc' ? 'ascend' : 'descend') : null
  );

  // ── Table columns ──────────────────────────────────────────────
  const columns = [
    {
      title: 'Clinic', dataIndex: 'name', key: 'name',
      ellipsis: { showTitle: false }, sorter: true, sortOrder: getSortOrder('name'),
      render: (v) => <Tooltip title={v}>{v}</Tooltip>,
    },
        {
      title: 'City', dataIndex: 'city', key: 'city',
      width: 130, sorter: true, sortOrder: getSortOrder('city'),
      ellipsis: { showTitle: false },
      render: (v) => v ? <Tooltip title={v}>{v}</Tooltip> : '—',
    },
    {
      title: 'State', dataIndex: 'state', key: 'state',
      width: 130, sorter: true, sortOrder: getSortOrder('state'),
      ellipsis: { showTitle: false },
      render: (v) => v ? <Tooltip title={v}>{v}</Tooltip> : '—',
    },
    {
      title: 'Contact', key: 'contact',
      width: 160,
      render: (_, record) => {
        const contact = record.mobile || record.landline || '—';
        return contact !== '—' ? <Tooltip title={contact}>{contact}</Tooltip> : '—';
      },
    },
    {
      title: 'Email', dataIndex: 'email', key: 'email',
      width: 220, ellipsis: { showTitle: false },
      sorter: true, sortOrder: getSortOrder('email'),
      render: (v) => v ? <Tooltip title={v}>{v}</Tooltip> : '—',
    },
    {
      title: 'Status', dataIndex: 'isActive', key: 'isActive',
      width: 100, sorter: true, sortOrder: getSortOrder('isActive'),
      render: (v) => (
        <Tag color={v ? 'green' : 'red'} style={{ borderRadius: 9999, fontSize: 11 }}>
          {v ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 90,
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="Edit">
            <Button
              shape="circle"
              size="small"
              icon={<EditOutlined />}
              style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
              onClick={() => { setEditRecord(record); setFormOpen(true); }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              shape="circle"
              size="small"
              icon={<DeleteOutlined />}
              danger
              style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }}
              onClick={() => showDeleteConfirm({
                content: 'Are You Sure You Want To Delete this clinic?',
                onOk: () => deleteMutation.mutate(record.id),
              })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className="clinic-list-page">
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 14 }}
        items={[
          {
            title: (
              <HomeOutlined
                onClick={() => navigate('/dashboard')}
                style={{ cursor: 'pointer' }}
              />
            ),
          },
          { title: 'Clinics' },
        ]}
      />

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div className="clinic-list-toolbar" style={{
        display:      'flex',
        alignItems:   'center',
        gap:          10,
        marginBottom: 16,
        background:   '#fff',
        borderRadius: 14,
        padding:      '10px 16px',
        boxShadow:    '0 2px 8px rgba(var(--wl-primary-rgb),0.08)',
        border:       '1px solid var(--wl-primary-pale)',
      }}>

        <Input
          className="clinic-list-search"
          prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)', fontSize: 14 }} />}
          placeholder="Search by name, city, state, mobile, email..."
          value={searchInput}
          onChange={handleSearchChange}
          allowClear
          style={{
            flex:         1,
            maxWidth:     280,
            borderRadius: 9999,
            borderColor:  'var(--wl-primary-pale)',
          }}
        /> 

        <Select
          value={statusFilter}
          onChange={(val) => { setStatusFilter(val); setPagination((p) => ({ ...p, page: 1 })); }}
          allowClear={!!statusFilter}
          onClear={() => { setStatusFilter(''); setPagination((p) => ({ ...p, page: 1 })); }}
          clearIcon={<Tooltip title="Clear filter"><CloseCircleOutlined /></Tooltip>}
          style={{ width: 130, borderRadius: 9999 }}
          options={[
            { label: 'All',      value: '' },
            { label: 'Active',   value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ]}
        />

        {/* Add button — right-aligned */}
        <Button
          className="clinic-list-add-btn"
          type="primary"
          icon={<PlusOutlined />}
          style={{ marginLeft: 'auto' }}
          onClick={() => { setEditRecord(null); setFormOpen(true); }}
        >
          Add Clinic
        </Button>
      </div>

      {/* ── Table ───────────────────────────────────────────── */}
      <DataTable
        columns={columns}
        dataSource={allClinics}
        loading={isLoading}
        rowKey="id"
        tableLayout="fixed"
        scroll={{ x: 800 }}
        pagination={{
          current:  pagination.page,
          pageSize: pagination.limit,
          total:    meta.total,
          onChange: (page, limit) => setPagination({ page, limit }),
        }}
        onChange={handleTableChange}
      />

      {/* ── Add / Edit form ──────────────────────────────────── */}
      <ClinicForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRecord(null); }}
        onSubmit={handleFormSubmit}
        initialValues={editRecord}
      />
    </div>
  );
};

export default ClinicList;
